import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { NetworkStatus, WatchQueryFetchPolicy } from "apollo-client";
import { intersection, isEmpty, size } from "lodash";
import { Collapser } from "@sonder/ui/source/styled/components/Collapser";
import { Checkbox } from "@sonder/ui/source/styled/fragments/Checkbox";
import {
  Scroller,
  ScrollerContent,
} from "@sonder/ui/source/styled/fragments/Scroller";
import { Table } from "@sonder/ui/source/styled/fragments/Table";
import { sizes } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useLayout } from "core/components/LayoutProvider";
import { ONE_MINUTE } from "core/constants";
import { usePageVisibilityPolling } from "core/hooks/usePageVisibilityPolling";
import { useRefetchableQuery } from "core/hooks/useRefetchableQuery";
import {
  GetTasksResponse,
  GetTasksVariables,
  TaskData,
  getTasks,
} from "core/queries/GetTasks";
import { useRefetchableGetTasksTotalQuery } from "core/queries/GetTasksTotal";
import { mapTaskDataToProps } from "core/queries/mappers";
import {
  collapseGroup,
  deselectTasks,
  expandGroup,
  selectTasks,
} from "tasks/actions";
import { EmptyRow } from "tasks/components/TaskList/EmptyRow";
import { HeaderRow } from "tasks/components/TaskList/HeaderRow";
import { Pagination } from "tasks/components/TaskList/Pagination";
import { Row } from "tasks/components/TaskList/Row";
import { TaskTitle } from "tasks/components/TaskList/TaskTitle";
import { FilterPair, intersectPriorityFilters } from "tasks/helpers";
import { useStore } from "tasks/store";
import {
  CleaningRequestCategory,
  TaskFilterInput,
  TaskSortInput,
} from "typings/globalGQLTypes";

// Helpers
const defaultGetTasksQueryProps = {
  total: 0,
  cursor: "0",
  hasMore: false,
  tasks: [] as TaskData[],
};
const mapDataToProps = (data?: GetTasksResponse) => {
  const {
    total,
    cursor: nextCursor,
    hasMore,
    tasks,
  } = data && data.taskConnections
    ? data.taskConnections
    : defaultGetTasksQueryProps;

  return {
    total,
    nextCursor,
    hasMore,
    tasks: tasks.map(mapTaskDataToProps),
  };
};

const mergeTaskListFilters = ({ globalFilters, localFilters }: FilterPair) => ({
  ...globalFilters,
  ...localFilters,
  ...intersectPriorityFilters({ globalFilters, localFilters }),
});

// Don't attempt to sort the "new" tasks list by due at date
const skipDueBySortForNewTasks = (
  filter: TaskFilterInput,
  sortOptions: TaskSortInput[] | null,
) => {
  if (!Array.isArray(sortOptions)) return [];
  if (
    filter.triaged === "new" &&
    sortOptions.some(({ column }) => column === "due_at")
  ) {
    return [];
  }
  return sortOptions;
};

export interface TaskListOuterContainerProps {
  isCollapsed: boolean;
}

// Components
export const TaskListOuterContainer = styled.div<TaskListOuterContainerProps>`
  box-sizing: border-box;
  max-width: ${sizes.listWidth};
  margin: 0 auto;
  width: 100%;
  padding: 1rem 1rem ${props => (props.isCollapsed ? "0" : "1rem")} 1.5rem;
`;

const StyledScroller = styled(Scroller)`
  border-radius: 0 0 0.3rem 0.3rem;
`;

// Exports
interface TaskListProps {
  id: CollapsedGroupId;
  className?: string;
  title?: string;
  limit: number;
  cursor?: string | null;
  filters: TaskFilterInput;
  showEmpty?: boolean;
  isActive?(props: FilterPair): boolean;
  pollInterval?: number;
}

export const TaskList = ({
  id: groupId,
  className,
  title = "Tasks",
  limit,
  cursor = "0",
  filters,
  showEmpty = false,
  isActive = () => true,
  pollInterval = ONE_MINUTE,
}: TaskListProps) => {
  const { sidepanel } = useLayout();
  const { track } = useSegment();
  const {
    state: { filters: globalFilters, sort, collapsedGroups, selectedTasks },
    dispatch,
  } = useStore();
  const [curCursor, setCurCursor] = useState(cursor);
  const mergedFilters = mergeTaskListFilters({
    globalFilters,
    localFilters: filters,
  });

  const isCollapsed = collapsedGroups[groupId];

  const skipGetTasksQuery =
    // City filter not set, so we can't query for tasks
    // unless we're simply querying for the user's assigned tasks
    (!mergedFilters.city && groupId !== "assigned") ||
    // List is disabled based on a combination of global and local filters
    !isActive({ globalFilters, localFilters: filters });

  const sharedVariables = {
    limit,
    cursor: curCursor,
    filter: {
      ...mergedFilters,
      cleaningRequestCategory: [CleaningRequestCategory.space_clean],
    },
    sort: skipDueBySortForNewTasks(mergedFilters, sort),
  };

  const sharedOptions = {
    fetchPolicy: "cache-and-network" as WatchQueryFetchPolicy,
    notifyOnNetworkStatusChange: true,
    pollInterval,
    variables: sharedVariables,
  };

  const {
    data,
    error,
    loading,
    networkStatus,
    startPolling,
    stopPolling,
    refetch,
  } = useRefetchableQuery<GetTasksResponse, GetTasksVariables>(getTasks, {
    ...sharedOptions,
    skip: isCollapsed || skipGetTasksQuery,
  });

  const {
    data: totalData,
    error: totalError,
    loading: totalLoading,
    networkStatus: totalNetworkStatus,
    startPolling: startTotalPolling,
    stopPolling: stopTotalPolling,
    refetch: totalRefetch,
  } = useRefetchableGetTasksTotalQuery({
    ...sharedOptions,
    skip: skipGetTasksQuery,
  });

  usePageVisibilityPolling({
    pollInterval,
    refetch: variables => (!isCollapsed ? refetch(variables) : undefined),
    startPolling: pI => {
      if (!isCollapsed) {
        startPolling(pI);
      }
    },
    stopPolling,
  });

  usePageVisibilityPolling({
    pollInterval,
    refetch: variables => (isCollapsed ? totalRefetch(variables) : undefined),
    startPolling: pI => {
      if (isCollapsed) {
        startTotalPolling(pI);
      }
    },
    stopPolling: stopTotalPolling,
  });

  useEffect(() => {
    if (isCollapsed) {
      startTotalPolling(pollInterval);
      stopPolling();
    } else {
      startPolling(pollInterval);
      stopTotalPolling();
    }
  }, [isCollapsed, pollInterval]);

  const { total, nextCursor, hasMore, tasks } = mapDataToProps(data);
  const trueTotal =
    total ||
    (totalData?.tomTasksCount && parseInt(totalData.tomTasksCount, 10)) ||
    0;
  const showPaginate = limit ? trueTotal > limit : false;

  const sortDependencyKey = JSON.stringify(sort);
  const filterDependencyKey = JSON.stringify(mergedFilters);
  useEffect(
    () => setCurCursor(cursor),
    [sortDependencyKey, filterDependencyKey],
  );

  const hasError = error || totalError;
  const isLoading = loading || totalLoading;
  const isPolling =
    networkStatus === NetworkStatus.poll ||
    totalNetworkStatus === NetworkStatus.poll;

  const isRefetching =
    networkStatus === NetworkStatus.refetch ||
    totalNetworkStatus === NetworkStatus.refetch;

  const hideList =
    // Hide the list when it's empty
    (!showEmpty &&
      ((!isCollapsed && tasks.length === 0) ||
        (isCollapsed && trueTotal === 0)) &&
      (isLoading === false || isPolling || isRefetching)) ||
    // Hide the list when we're not querying
    skipGetTasksQuery;

  const visibleTaskIds = tasks.map(({ id }) => id);
  const visibleAndSelected = size(intersection(selectedTasks, visibleTaskIds));
  const isAllSelected =
    visibleAndSelected > 0 && visibleAndSelected === size(visibleTaskIds);
  const isNoneSelected = visibleAndSelected === 0;

  return hideList ? null : (
    <TaskListOuterContainer isCollapsed={isCollapsed}>
      <TaskTitle
        count={trueTotal}
        isCollapsed={isCollapsed}
        title={title}
        onClick={() => {
          if (isCollapsed) {
            dispatch(expandGroup(groupId));
          } else {
            dispatch(collapseGroup(groupId));
          }
        }}
      />
      <Collapser isCollapsed={isCollapsed}>
        <StyledScroller>
          <ScrollerContent>
            <Table
              className={className}
              columns={
                sidepanel === "collapsed"
                  ? `3rem 1.5rem minmax(10rem, 1fr) 5.5rem max-content 7.5rem 9.5rem 7rem 7rem 8rem 3.5rem`
                  : `3rem 1.5rem minmax(10rem, 1fr) 5.5rem max-content 7.5rem 9.5rem 4rem 3.5rem`
              }
            >
              <HeaderRow
                checkbox={
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={!isNoneSelected && !isAllSelected}
                    onClick={() => {
                      track({
                        eventName: "task_list",
                        action: "click",
                        element: "selectTasks",
                        props: {
                          page: curCursor,
                          groupId,
                          selected: !isAllSelected,
                        },
                      });
                      if (isNoneSelected) {
                        dispatch(selectTasks(visibleTaskIds));
                      } else {
                        dispatch(deselectTasks(visibleTaskIds));
                      }
                    }}
                  />
                }
                title="Tasks"
              />
              {isEmpty(tasks) && (
                <EmptyRow
                  errorLabel={hasError ? "Something went wrong" : undefined}
                  label="No tasks"
                  loadingLabel={isLoading ? "Loading..." : undefined}
                />
              )}
              {tasks.map(({ id, ...rest }, index) => (
                <Row
                  id={id}
                  key={id}
                  {...rest}
                  lastRow={index === tasks.length - 1}
                />
              ))}
            </Table>
          </ScrollerContent>
        </StyledScroller>
        {showPaginate && (
          <Pagination
            cursor={curCursor}
            hasNextPage={hasMore}
            onNextClick={() => setCurCursor(nextCursor)}
            onPrevClick={setCurCursor}
          />
        )}
      </Collapser>
    </TaskListOuterContainer>
  );
};
